import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import CampOpsNav from "@/components/CampOpsNav";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, ClipboardList } from "lucide-react";
import { METRIC_LABELS, ALL_METRICS } from "@/lib/metrics";

export default function CampBulkEntry() {
  const { campId } = useParams();
  const [metricType, setMetricType] = useState("exit_velocity");
  const [verification, setVerification] = useState("cbr_verified");
  const [roster, setRoster] = useState(null);
  const [values, setValues] = useState({}); // player_id -> string
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setRoster(null);
    setValues({});
    api.get(`/admin/camps/${campId}/roster`, { params: { metric_type: metricType } })
      .then(({ data }) => setRoster(data))
      .catch(() => setRoster({ players: [] }));
  }, [campId, metricType]);

  useEffect(() => { load(); }, [load]);

  const saveAll = async () => {
    const entries = Object.entries(values)
      .filter(([, v]) => v !== "" && v != null && !isNaN(Number(v)))
      .map(([player_id, v]) => ({ player_id, value: Number(v) }));
    if (entries.length === 0) {
      toast.error("Enter at least one value");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/camps/${campId}/metrics/bulk`, {
        metric_type: metricType,
        verification_status: verification,
        entries,
      });
      toast.success(`Saved ${data.created} ${METRIC_LABELS[metricType]} results`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const filledCount = Object.values(values).filter((v) => v !== "" && v != null && !isNaN(Number(v))).length;

  return (
    <AdminLayout>
      <div className="p-8 max-w-5xl">
        <Link to="/admin/camps" className="label-overline text-slate-500 hover:text-[#E63946]">← All Camps</Link>
        <div className="label-overline text-[#E63946] mt-3">Bulk Metric Entry</div>
        <h1 data-testid="bulk-entry-title" className="font-display text-4xl uppercase tracking-tighter mt-2">
          {roster?.camp?.title || "Camp"}
        </h1>

        <CampOpsNav campId={campId} />

        {/* Controls */}
        <div className="flex flex-wrap items-end gap-4 mb-5">
          <label className="flex flex-col gap-1">
            <span className="label-overline text-[10px]">Metric / Station</span>
            <select data-testid="bulk-metric-select" value={metricType} onChange={(e) => setMetricType(e.target.value)} className="border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white min-w-[200px]">
              {ALL_METRICS.map((m) => <option key={m} value={m}>{METRIC_LABELS[m]}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="label-overline text-[10px]">Source</span>
            <select data-testid="bulk-verification-select" value={verification} onChange={(e) => setVerification(e.target.value)} className="border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white">
              <option value="cbr_verified">CBR Verified</option>
              <option value="staff_entered">Staff Entered</option>
            </select>
          </label>
          <button
            data-testid="bulk-save-all"
            onClick={saveAll}
            disabled={saving || filledCount === 0}
            className="inline-flex items-center gap-2 bg-[#E63946] text-white px-6 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : `Save All (${filledCount})`}
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-[#E63946]" />
            <span className="font-display uppercase tracking-tight">{METRIC_LABELS[metricType]} Station</span>
            <span className="ml-auto label-overline">{roster?.units || ""}</span>
          </div>
          {!roster ? (
            <div className="p-8 label-overline text-slate-500">Loading roster…</div>
          ) : roster.players.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No players registered for this camp.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left p-3 label-overline w-16">#</th>
                  <th className="text-left p-3 label-overline">Player</th>
                  <th className="text-left p-3 label-overline">Pos</th>
                  <th className="text-left p-3 label-overline">Current</th>
                  <th className="text-left p-3 label-overline w-40">New Result ({roster.units})</th>
                </tr>
              </thead>
              <tbody>
                {roster.players.map((p) => (
                  <tr key={p.player_id} data-testid={`bulk-row-${p.player_id}`} className="border-b border-slate-100 last:border-0">
                    <td className="p-3 font-data text-lg">{p.player_number ?? "—"}</td>
                    <td className="p-3 font-semibold">{p.first_name} {p.last_name}</td>
                    <td className="p-3 font-data">{p.primary_position || "—"}</td>
                    <td className="p-3 font-data text-slate-500">{p.current_value != null ? p.current_value : "—"}</td>
                    <td className="p-3">
                      <input
                        data-testid={`bulk-input-${p.player_id}`}
                        type="number"
                        step="0.1"
                        inputMode="decimal"
                        value={values[p.player_id] ?? ""}
                        onChange={(e) => setValues((v) => ({ ...v, [p.player_id]: e.target.value }))}
                        className="w-32 px-3 py-2 border border-slate-300 rounded-sm font-data text-lg"
                        placeholder="—"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
